/**
 * Phase 0 mobile shell (docs/mobile-design.md §3): top bar (avatar → Me, search, Ask, notifications), the page, and the
 * role-shaped bottom tab bar with the centre ＋ Create. Rendered by AppShell on small touch screens instead of the
 * sidebar layout. Safe areas: the top bar pads by the top inset, the tab bar by the bottom inset.
 */
import { PlatformBanner } from '@/features/billing/PlatformBanner';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Bell, CalendarClock, CalendarDays, Coffee, LogIn, LogOut, CalendarRange, CheckSquare, ClipboardCheck, Fuel, ListChecks, Hand, Megaphone, Newspaper, MessageSquare, MessagesSquare, Award, Plane, Plus, Search, Siren, Sparkles, Truck, Video, Wrench, X, type LucideIcon } from 'lucide-react';
import { activeMobileTabFrom, isMorePath, MOBILE_TAB_LABEL, MOBILE_TAB_PATH, TOP_PANEL_PREFIX, desktopOnlyScreen, phoneRotaLink, isFullScreenChat, mobileTabBadges, toggleTopPanel, topBarShift, phonePersonSettings, phoneWorkspaceSetting, topPanelFromHistory, CLOCK_ACTION_LABEL, type CreateItem, type MeToday, type CreateItemId, type MobileTabId, type TopPanel } from '@tradeoutlet/shared';
import { useMeToday, useNotifications } from '@tradeoutlet/data';
import { RealtimeBridge } from '@/lib/RealtimeBridge';
import { SettingsReturnBar, useBackClosesDrawer } from '@/lib/origin';
import { NavAreaTracker, useNavBadgeItems } from '@/lib/navArea';
import { useMe, useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { Avatar, Spinner } from '@/components/ui';
import { MobileTaskDetail } from './TaskDetail';
import { IncomingCallModal } from '@/features/calls/IncomingCallModal';
import { PersonDrawer } from '@/features/people/PersonDrawer';
import { FeedbackDialog } from '@/features/feedback/Feedback';
import { NewRoomModal } from '@/features/rooms/NewRoomModal';
import { DesktopOnlyCard } from './DesktopOnlyCard';
import { PhonePersonSettings } from './settings/PersonSettings';
import { PhoneWorkspaceSettings } from './settings/WorkspaceSettings';
import { useMobileTabs } from './useMobileTabs';
import { useKeyboard } from './keyboard';
import { useClockFlow, WaitingMarker } from './TodayPage';
import { OfflineBar, ShellOfflineBar } from './OfflineBar';
import { MoreTab, useMoreItems } from './MoreMenu';
import { BuildingLink } from './buildings/common';
import { PhoneAssistant, usePhoneAssistant } from './AssistantScreen';
import { holdsAnywhere } from '@/lib/navAccess';
import { usePermissions } from '@/lib/permissions';
import { ModuleRoute } from '@/lib/modules';
import { useHandoverTemplatesAvailable } from '@/features/workforce/handovers/available';

const CallRoom = lazy(() => import('@/features/calls/CallRoom'));
const MePage = lazy(() => import('./MePage'));
const NotificationsPage = lazy(() => import('./NotificationsPage'));

/**
 * t183u: Me and notifications open as a panel over the current page (which stays mounted, so its route and scroll are
 * untouched). The same button closes it, the other button switches panels, and Back closes it. A link followed from
 * the panel leaves its history entry behind; coming Back to that entry reopens the panel.
 */
export function useTopPanel() {
  const [panel, setPanel] = useState<TopPanel | null>(null);
  useBackClosesDrawer(Boolean(panel), () => setPanel(null), panel ? `${TOP_PANEL_PREFIX}${panel}` : true);
  const loc = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setPanel(topPanelFromHistory((window.history.state as { drawer?: unknown } | null)?.drawer));
  }, [loc.key]);
  return { panel, setPanel };
}

const TAB_ICON: Record<MobileTabId, LucideIcon> = { home: Newspaper, chat: MessagesSquare, shifts: CalendarClock, rota: CalendarRange, tasks: CheckSquare, runs: Truck };
const CREATE_ICON: Record<CreateItemId, LucideIcon> = {
  issue: Siren, task: CheckSquare, message: MessageSquare, 'time-off': Plane, kudos: Award, event: CalendarDays, announcement: Megaphone, handover: Hand,
  'meeting-room': Video, walkaround: ClipboardCheck, defect: Wrench, fuel: Fuel, checklist: ListChecks,
};

export function MobileShell() {
  const taskPanelId = useUi(s => s.taskPanelId);
  const openTask = useUi(s => s.openTask);
  useBackClosesDrawer(Boolean(taskPanelId), () => openTask(null));
  // Inputs 16px+ while the phone shell is up (index.css), so iOS Safari doesn't zoom on focus; modals render outside this tree.
  useEffect(() => { document.documentElement.classList.add('mobile'); return () => document.documentElement.classList.remove('mobile'); }, []);
  const activeCall = useUi(s => s.activeCall);
  const [creating, setCreating] = useState(false);
  const { pathname, search } = useLocation();
  // t366u: a rota notification's desktop schedule link opens the phone rota on that day and building.
  const rotaLink = phoneRotaLink(pathname, search);
  // t331u B: person settings tabs with a phone page show it; the rest of Settings stays "best on a computer".
  const phoneSettings = phonePersonSettings(pathname, search);
  const wsSetting = phoneSettings ? null : phoneWorkspaceSetting(pathname);
  const desk = phoneSettings || wsSetting ? null : desktopOnlyScreen(pathname);
  // Conversations and threads are full screen (their header has Back); the keyboard pins the shell to the visible area.
  const full = isFullScreenChat(pathname);
  const kb = useKeyboard();
  const top = useTopPanel();
  const assistant = usePhoneAssistant();
  return (
    <ShellOfflineBar.Provider value>
    <div className={clsx('mobile-shell flex flex-col overflow-hidden bg-white dark:bg-slate-950', kb.inset ? 'fixed inset-x-0' : 'h-full')} data-testid="mobile-shell"
      style={kb.inset ? { top: kb.top, height: kb.height } : undefined}>
      <RealtimeBridge />
      <NavAreaTracker />
      {!full && <MobileTopBar panel={top.panel} onPanel={top.setPanel} />}
      {!full && <PlatformBanner />}
      {!full && <OfflineBar />}
      {!full && <SettingsReturnBar />}
      <main id="main" tabIndex={-1} className="relative flex min-h-0 flex-1 outline-none">
        <div className="min-w-0 flex-1 overflow-hidden" aria-hidden={top.panel ? true : undefined}><ModuleRoute>{rotaLink ? <Navigate to={rotaLink} replace /> : phoneSettings ? <PhonePersonSettings key={pathname + search} {...phoneSettings} /> : wsSetting ? <PhoneWorkspaceSettings key={pathname} setting={wsSetting} /> : desk ? <DesktopOnlyCard name={desk.name} back="/" /> : <Outlet />}</ModuleRoute></div>
        {top.panel && !full && (
          <div className="absolute inset-0 z-20 bg-white dark:bg-slate-950" role="region" aria-label={top.panel === 'me' ? 'Me' : 'Notifications'} data-testid="top-panel" data-panel={top.panel}>
            <Suspense fallback={null}>{top.panel === 'me' ? <MePage /> : <NotificationsPage />}</Suspense>
          </div>
        )}
      </main>
      {!full && !kb.inset && <MobileTabBar onCreate={() => setCreating(true)} creating={creating} />}
      {/* Task detail is a full screen on the phone (mobile Phase 2); Back closes it. */}
      {taskPanelId && <MobileTaskDetail key={taskPanelId} taskId={taskPanelId} />}
      {assistant && <PhoneAssistant />}
      <CreateSheet open={creating} onClose={() => setCreating(false)} />
      <PersonDrawer />
      <FeedbackDialog />
      {activeCall && <Suspense fallback={null}><CallRoom /></Suspense>}
      <IncomingCallModal />
      <NewRoomModal />
    </div>
    </ShellOfflineBar.Provider>
  );
}

const iconBtn = 'relative flex h-11 w-11 items-center justify-center rounded-full text-slate-700 active:bg-slate-100 dark:text-slate-200 dark:active:bg-slate-800';
/** t301u: the top bar's Me, Search, Ask and bell: full height, a little narrower (36px under 390px) so the shift keeps its room. */
export const topBarBtn = 'relative flex h-11 w-10 shrink-0 items-center justify-center rounded-full text-slate-700 active:bg-slate-100 dark:text-slate-200 dark:active:bg-slate-800 max-[389px]:w-9';

export function MobileTopBar({ panel, onPanel }: { panel: TopPanel | null; onPanel: (p: TopPanel | null) => void }) {
  const ws = useWorkspaceId();
  const me = useMe().data?.profile;
  const unread = useNotifications(ws).data?.unread_count ?? 0;
  const { pathname } = useLocation();
  const nav = useNavigate();
  // t301u: Ask, gated as on the desktop (ai.assistant.use held anywhere); opens the assistant full screen.
  const canAsk = holdsAnywhere(usePermissions().data, 'ai.assistant.use');
  const openAssistant = useUi(s => s.openAssistant);
  const press = (p: TopPanel) => {
    // Opened as a page (/me, /notifications from a link): the same button leaves it.
    if (!panel && pathname === (p === 'me' ? '/me' : '/notifications')) {
      const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
      if (idx > 0) nav(-1); else nav('/');
      return;
    }
    onPanel(toggleTopPanel(panel, p));
  };
  return (
    <header className="flex shrink-0 items-center gap-0.5 border-b border-slate-200 bg-white px-1 pt-[env(safe-area-inset-top)] dark:border-slate-800 dark:bg-slate-950" data-testid="mobile-topbar">
      <button type="button" onClick={() => press('me')} className={clsx(topBarBtn, panel === 'me' && 'bg-slate-100 dark:bg-slate-800')} aria-label="Me" aria-expanded={panel === 'me'} data-testid="mobile-me">
        <Avatar size={32} name={me?.display_name} id={me?.user_id} url={me?.avatar_url} />
      </button>
      <TopBarShiftSlot />
      <Link to="/search" className={topBarBtn} aria-label="Search" data-testid="mobile-search"><Search className="h-5 w-5" /></Link>
      {canAsk && <button type="button" onClick={() => openAssistant()} className={topBarBtn} aria-label="Ask" data-testid="mobile-ask"><Sparkles className="h-5 w-5" /></button>}
      <button type="button" onClick={() => press('notifications')} className={clsx(topBarBtn, panel === 'notifications' && 'bg-slate-100 dark:bg-slate-800')} aria-expanded={panel === 'notifications'}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'} data-testid="mobile-bell">
        <Bell className="h-5 w-5" />
        {unread > 0 && <span className="absolute right-0.5 top-0.5 min-w-5 rounded-full bg-accent px-1 text-center text-xs font-bold leading-5 text-on-accent ring-1 ring-black">{unread > 99 ? '99+' : unread}</span>}
      </button>
    </header>
  );
}

/**
 * t184u: my shift in the top bar, between Me and search, on every tab: now (else next) as time + building, and one clock
 * button through the same flow as the Today clock card (geofence, location-check sheet, idempotent clock-in). Hidden
 * when there's nothing to show (no shift and nowhere to clock in, e.g. people without an employee record).
 */
function TopBarShiftSlot() {
  const ws = useWorkspaceId();
  const t = useMeToday(ws).data;
  if (!t) return <div className="min-w-0 flex-1" />;
  return <TopBarShiftView t={t} />;
}
export function TopBarShiftView({ t }: { t: MeToday }) {
  const flow = useClockFlow(t);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(i); }, []);
  // t187u: the clock as it will be once punches saved offline are sent.
  const v = topBarShift({ ...t, clock: flow.clock }, now);
  if (!v) return <div className="min-w-0 flex-1" />;
  const Icon = v.action ? { in: LogIn, out: LogOut, break: Coffee, resume: Coffee }[v.action] : null;
  const to = v.shiftId ? `/workforce/my-shifts/${v.shiftId}` : '/today';
  const label = v.state === 'out' ? 'My shift' : v.state === 'break' ? 'On break' : 'Clocked in';
  return (
    <div className="flex min-w-0 flex-1 items-center gap-1.5 pl-0.5" data-testid="topbar-shift" data-state={v.state} data-waiting={flow.waiting || undefined}>
      <Link to={to} className="flex min-w-0 flex-1 flex-col justify-center overflow-hidden rounded-lg px-1 py-0.5 leading-tight active:bg-slate-100 dark:active:bg-slate-800" data-testid="topbar-shift-open"
        aria-label={`${label}: ${v.time}${v.place ? `, ${v.place}` : ''}${flow.waiting ? ', waiting to send' : ''}`}>
        {/* A punch saved offline: "Clocked in" with a small waiting-to-send marker until the server has it. */}
        <span className={clsx('truncate whitespace-nowrap text-sm font-semibold tabular-nums', v.state === 'in' && 'text-emerald-700 dark:text-emerald-400', v.state === 'break' && 'text-orange-700 dark:text-orange-300')} data-testid="topbar-shift-time">{flow.waiting && v.state !== 'out' ? label : v.time}</span>
        {flow.waiting ? <WaitingMarker className="truncate" short /> : v.place && <span className="truncate text-xs text-slate-500" data-testid="topbar-shift-place">{v.placeId ? <BuildingLink id={v.placeId} name={v.place} /> : v.place}</span>}
      </Link>
      {/* t187u: Start break as a compact icon button next to Clock out (fits at 375px). */}
      {v.secondary === 'break' && (
        <button type="button" onClick={() => void flow.tap('break')} disabled={flow.busy} data-testid="topbar-break" aria-label="Start break" title="Start break"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-slate-300 text-slate-800 active:bg-slate-100 disabled:opacity-60 dark:border-slate-700 dark:text-slate-100 dark:active:bg-slate-800">
          <Coffee className="h-[18px] w-[18px]" aria-hidden />
        </button>
      )}
      {v.action && Icon && (
        <button type="button" onClick={() => void flow.tap(v.action!)} disabled={flow.busy} data-testid="topbar-clock"
          className={clsx('flex h-9 shrink-0 items-center gap-1 whitespace-nowrap rounded-xl px-2.5 text-sm font-semibold disabled:opacity-60 max-[389px]:px-2',
            v.action === 'in' ? 'bg-accent text-on-accent ring-1 ring-inset ring-black/10' : v.action === 'out' ? 'bg-ink text-white dark:bg-white dark:text-ink' : 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white')}>
          {/* At 375px the icon goes so the time keeps its room; the label always stays. */}
          {flow.busy ? <Spinner className="h-4 w-4" /> : <Icon className="h-4 w-4 max-[429px]:hidden" aria-hidden />}{CLOCK_ACTION_LABEL[v.action]}
        </button>
      )}
      {flow.sheetsEl}
    </div>
  );
}

export function MobileTabBar({ onCreate, creating }: { onCreate: () => void; creating: boolean }) {
  const { slots } = useMobileTabs();
  const { pathname, key } = useLocation();
  const nav = useNavigate();
  const { items, chat } = useNavBadgeItems();
  const badges = mobileTabBadges(items, slots, chat);
  const more = useMoreItems();
  // B3: an event detail keeps the tab it was opened from (Home, the feed…). Remembered per history entry, so Back
  // and Forward light the same tab again; with no earlier page (a push or pasted link) it falls back to ⋯.
  const lit = useRef<{ byKey: Map<string, MobileTabId | null>; last?: MobileTabId | null }>({ byKey: new Map() });
  const base = lit.current.byKey.has(key) ? lit.current.byKey.get(key)! : activeMobileTabFrom(pathname, slots, more, lit.current.last);
  useEffect(() => { lit.current.byKey.set(key, base); lit.current.last = base; }, [key, base]);
  const active = creating ? null : base;
  return (
    <nav className="on-dark flex shrink-0 items-stretch bg-chrome-bar px-1 pb-[env(safe-area-inset-bottom)]" aria-label="Main" data-testid="mobile-tabbar">
      {slots.map(s => {
        if (s === 'create') {
          return (
            <div key="create" className="flex flex-1 items-center justify-center">
              <button type="button" onClick={onCreate} aria-label="Create" aria-expanded={creating} data-testid="tab-create"
                className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-on-accent ring-2 ring-chrome-bar active:bg-accent-pressed">
                <Plus className="h-6 w-6" strokeWidth={2.5} />
              </button>
            </div>
          );
        }
        const Icon = TAB_ICON[s];
        const on = active === s;
        const n = badges[s] ?? 0;
        return (
          <button key={s} type="button" data-testid={`tab-${s}`} aria-current={on ? 'page' : undefined}
            aria-label={n ? `${MOBILE_TAB_LABEL[s]}, ${n} waiting` : MOBILE_TAB_LABEL[s]}
            onClick={() => nav(MOBILE_TAB_PATH[s])}
            className={clsx('relative flex min-h-[56px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-xs font-medium', on ? 'text-white' : 'text-white/70')}>
            <span className={clsx('flex h-7 w-12 items-center justify-center rounded-full', on && 'bg-accent text-on-accent')}><Icon className="h-5 w-5" /></span>
            <span className="truncate">{MOBILE_TAB_LABEL[s]}</span>
            {n > 0 && <span className="absolute right-[14%] top-1 min-w-5 rounded-full bg-accent px-1 text-center text-xs font-bold leading-5 text-on-accent ring-1 ring-chrome-bar">{n > 99 ? '99+' : n}</span>}
          </button>
        );
      })}
      <MoreTab lit={!creating && base === null && isMorePath(pathname, more)} />
    </nav>
  );
}

/** ＋ Create: a bottom sheet of large rows, ordered by role. */
export function CreateSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { create: all } = useMobileTabs();
  // t692u: handovers need a template, so ＋ Handover only shows when one applies.
  const handover = useHandoverTemplatesAvailable(null, all.some(i => i.id === 'handover'));
  const create = all.filter(i => i.id !== 'handover' || handover === true);
  const nav = useNavigate();
  const setNewRoomOpen = useUi(s => s.setNewRoomOpen);
  const loc = useLocation();
  useEffect(() => { onClose(); }, [loc.pathname, loc.search]); // eslint-disable-line react-hooks/exhaustive-deps -- close on route change only
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [open, onClose]);
  // t222u: Issue from a building's page starts on that building (else the report picks where you are).
  const here = /^\/buildings\/([0-9a-f-]{36})/.exec(loc.pathname)?.[1];
  const pick = (i: CreateItem) => { onClose(); if (i.to) nav(i.id === 'issue' && here ? `${i.to}&location_id=${here}` : i.to); else if (i.id === 'meeting-room') setNewRoomOpen(true); };
  return (
    <div className={clsx('fixed inset-0 z-50', !open && 'pointer-events-none')} aria-hidden={!open}>
      <div className={clsx('absolute inset-0 bg-black/50 transition-opacity', open ? 'opacity-100' : 'opacity-0')} onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label="Create" data-testid="create-sheet"
        className={clsx('absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white pb-[calc(env(safe-area-inset-bottom)+8px)] shadow-2xl transition-transform duration-150 dark:bg-slate-900', open ? 'translate-y-0' : 'invisible translate-y-full')}>
        <div className="flex items-center justify-between px-4 pt-2">
          <span className="mx-auto h-1 w-10 rounded-full bg-slate-300 dark:bg-slate-700" aria-hidden />
        </div>
        <div className="flex items-center justify-between px-4 pb-1">
          <h2 className="text-base font-semibold">Create</h2>
          <button type="button" onClick={onClose} className={iconBtn} aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        <ul>
          {create.map(i => {
            const Icon = CREATE_ICON[i.id];
            return (
              <li key={i.id}>
                <button type="button" onClick={() => pick(i)} data-testid={`create-${i.id}`}
                  className="flex min-h-[56px] w-full items-center gap-3 px-4 text-left text-base active:bg-slate-100 dark:active:bg-slate-800">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"><Icon className="h-5 w-5" /></span>
                  {i.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
